// Copyright (c) Docugami, Inc. All rights reserved.

import { chunkHoverColor, chunkSelectedColor, lightActionBlue } from "components/shell/presentation/AppStyles";
import { DgmlInstancesSingleton } from "hooks/documentDgml";
import { ColorComponent, DefaultControlColorComponent } from "sdv/components/ColorComponent";
import { PreviewChunkComponent } from "sdv/components/PreviewChunkComponent";
import { ComponentId } from "sdv/engine/Component";
import { IEntityManager } from "sdv/engine/Entity";
import { createPreviewChunkEntity } from "sdv/engine/EntityFactory";
import { InputState } from "sdv/input/InputController";
import { renderEntity } from "sdv/utils/EntityHelpers";
import { IResources } from "../Resources";
import { ISystem, Queries } from "../engine/Engine";

/**
 * Manages generic rendering of chunks based on hover, selection, chunk toolbar, etc.
 */
export class ControlRenderSystem implements ISystem {
    get updateEnabled() { return true; }

    resources!: IResources;
    entities!: IEntityManager;
    input!: InputState;
    queries: Queries = {
        allChunks: { types: [ComponentId.Control] },
        hoverChunks: { types: [ComponentId.Hover] },
        selectedChunks: { types: [ComponentId.Selected] },
        previewChunks: { types: [ComponentId.PreviewChunk, ComponentId.Color] },
        multiSelectChunks: { types: [ComponentId.MultiSelect] }
    }

    update() {
        this.updatePreviewChunks();
        this.updateControlRenderChunks();
        this.updateMultiSelectChunks();
    }

    private updatePreviewChunks() {
        if (!this.resources.check(["previewChunks", "dgml"])) {
            return;
        }

        const previewChunks = this.resources.get("previewChunks").value;
        const activePreviewChunks = this.queries.previewChunks.query!.entities;
        for (const toRemove of activePreviewChunks.filter(e => !previewChunks[e.getComponent<PreviewChunkComponent>(ComponentId.PreviewChunk)!.spanId])) {
            toRemove.destroy();
        }

        for (const [spanId, color] of Object.entries(previewChunks)) {
            const chunk = DgmlInstancesSingleton.getInstanceById(this.resources.get("documentId").value!, spanId)?.chunk;
            const existingPreviewEntity = activePreviewChunks.find(e => e.getComponent<PreviewChunkComponent>(ComponentId.PreviewChunk)!.spanId === spanId);
            if (!chunk?.transient) {
                // Preview color will be applied in updateRenderChunks
                existingPreviewEntity?.destroy();
            }
            else if (!!existingPreviewEntity) {
                existingPreviewEntity.getComponent<ColorComponent>(ComponentId.Color)!.color = color;
            }
            else {
                createPreviewChunkEntity(this.entities, chunk, color);
            }
        }
    }

    private updateMultiSelectChunks() {
        for (const entity of this.queries.multiSelectChunks.query!.entities) {
            renderEntity(entity);
            entity.ensureComponent<ColorComponent>({
                typeId: ComponentId.Color,
                color: DefaultControlColorComponent.color
            });
        }
    }

    private updateControlRenderChunks() {
        const renderDisabled = this.resources.get("controlRenderDisabled").value;
        if (renderDisabled) {
            return;
        }

        const selectedChunk = this.queries.selectedChunks.query!.entities[0];
        renderEntity(selectedChunk);

        const highlightEntityName = this.resources.get("chunkToolbarHoverSpanId").value ?? '';
        const chunkToolbarHighlightEntity = this.entities.get(highlightEntityName);

        const previewChunks = this.resources.get("previewChunks").value;
        const hoverEntity = this.queries.hoverChunks.query!.entities[0];

        for (const chunk of this.queries.allChunks.query!.entities) {
            const isHoverChunk = chunk.name === hoverEntity?.name;
            const isSelectedChunk = chunk.name === selectedChunk?.name;
            const previewChunkColor = previewChunks[chunk.name];
            const isActiveChunk = isHoverChunk || isSelectedChunk || chunk.name === chunkToolbarHighlightEntity?.name || !!previewChunkColor;
            const shouldRender = isActiveChunk;
            if (!shouldRender) {
                renderEntity(chunk, true);
            }
            else {
                let color = DefaultControlColorComponent.color;
                if (isSelectedChunk) {
                    color = chunkSelectedColor;
                }
                else if (!!previewChunkColor) {
                    color = previewChunkColor;
                }
                else if (isHoverChunk) {
                    color = chunkHoverColor;
                }

                // Handle outflow control rendering differently
                if (chunk.hasComponent(ComponentId.Outflow)) {
                    color = lightActionBlue;
                }

                renderEntity(chunk);
                chunk.ensureComponent<ColorComponent>({
                    typeId: ComponentId.Color,
                    color: color
                }, true);
            }
        }
    }
}
